import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import classNames from 'classnames';
import { Tab, Table, Pagination, Icon, Checkbox, Button } from 'semantic-ui-react';
import ConvertNumberToText from './ConvertNumberToText';

import InvoiceElectricityHtml from './InvoiceElectricityHtml';
import InvoiceWaterHtml from './InvoiceWaterHtml';
import InvoiceGasHtml from './InvoiceGasHtml';

import styles from './InvoicePane.module.scss';

const InvoicePane = React.memo(({ defaultData, name, isEditable }) => {
  const [t] = useTranslation();

  const [dataTable, setDataTable] = useState([]);
  const [currentPage, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);

  const resultsPerPage = 10;
  const totalPages = Math.ceil(defaultData.length / resultsPerPage);

  const onPageInvoiceChange = (e, { activePage }) => {
    setPage(activePage);
    setSelectedIds({});
    setIsSelectAll(false);
  };

  // useEffect(() => {
  //   setDataTable([...defaultData]);
  // }, [defaultData]);
  useEffect(() => {
    setDataTable(
      defaultData.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage),
    );
  }, [defaultData, currentPage]);

  const handleOnRowSelect = useCallback(
    (e, { id, checked }) => {
      const newSelectedIds = {
        ...selectedIds,
        ...{ [id]: checked },
      };
      setSelectedIds(newSelectedIds);
      setIsSelectAll(
        Object.keys(newSelectedIds).length === dataTable.length &&
          Object.values(newSelectedIds).every((s) => s),
      );
    },
    [dataTable.length, selectedIds],
  );

  const handleOnSelectAll = useCallback(
    (e, { checked }) => {
      if (dataTable.length === 0) return;
      const newSelectedIds = dataTable
        .map((d) => ({ [d.id]: checked }))
        .reduce((acc, cur) => {
          const key = Object.keys(cur)[0];
          acc[key] = cur[key];
          return acc;
        });

      setSelectedIds(newSelectedIds);
      setIsSelectAll(
        Object.keys(newSelectedIds).length === dataTable.length &&
          Object.values(newSelectedIds).every((s) => s),
      );
    },
    [dataTable],
  );

  const handleOnPrint = useCallback(() => {
    let dataPrint = dataTable.filter((it) => selectedIds[it.id] === true);
    dataPrint = dataPrint.map((dt) => {
      let sumText = ConvertNumberToText(dt.sum * 1.1).trim();
      sumText = sumText.charAt(0).toUpperCase() + sumText.slice(1);

      return {
        ...dt,
        startDate: format(new Date(dt.startDate), 'dd/MM/yyyy'),
        endDate: format(new Date(dt.endDate), 'dd/MM/yyyy'),
        month: format(new Date(dt.startDate), 'MM/yyyy'),
        sumText,
      };
    });
    let html = InvoiceElectricityHtml(dataPrint);
    if (name === 'water') {
      html = InvoiceWaterHtml(dataPrint);
    } else if (name === 'gas') {
      html = InvoiceGasHtml(dataPrint);
    }
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'display: none';

    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;

    doc.open('text/html', 'replace');

    const htmlPrint = `<!doctype html><html><head>
    <style>
          @media print {
            @page {
              size: A4;
              margin: 14mm 12mm 35mm 20mm;
              }
            body {
              font-family: 'Times New Roman', Roboto, Arial, Tahoma;
              width: 100%;
              margin: 0;
              padding: 0;
              line-height: 1.15em;
            }
            a {
              color: #FFFFFF;
              text-decoration: none;
            }
            table, td, th {
              border: 1px solid black;
              text-align: center;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
            }
            table td {
              padding: 0.25rem 0.5rem;
              text-align: right;
            }
            table th {
              font-weight: 700;
              padding: 0.25rem 0.5rem;
            }
            table .tableFooter td {
              background-color: #3D85C6 !important;
              font-weight: 700;
            }
            table .noBorderRight {
              border-right-width: 0;
            }
            table .noBorderLeft {
              border-left-width: 0;
            }
            .center {
              text-align: center;
            }
            .invoice-title {
              text-align: center;
              margin-top: -1rem;
            }
            .header {
              display: grid;
              grid-template-columns: 2fr 1fr;
              grid-template-rows: 2fr 1fr;
              grid-column-gap: 0;
            }
            .header .header-item {
              display: grid;
              grid-template-columns: 2fr 1fr;
              grid-template-rows: 2fr 1fr;
              grid-column-gap: 0;
              text-align: right;
            }
            .footer {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-template-rows: 1fr 1fr;
              grid-column-gap: 0;
              text-align: center;
            }
            .pagebreak { page-break-before: always; }
          }
        </style>
    </head><body>
      ${html}
    </body></html>`;

    doc.write(htmlPrint);
    doc.close();

    doc.defaultView.print();

    setTimeout(() => {
      iframe.parentElement.removeChild(iframe);
    }, 10);
  }, [dataTable, name, selectedIds]);

  return (
    <Tab.Pane className={styles.wrapper}>
      {isEditable && (
        <Button
          className={styles.buttonPrint}
          icon="print"
          onClick={handleOnPrint}
          content={t('action.printInvoice')}
        />
      )}
      <div className={styles.tableWrapper}>
        <Table singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox
                  fitted
                  indeterminate={!isSelectAll && Object.values(selectedIds).some((s) => s === true)}
                  checked={isSelectAll}
                  onChange={handleOnSelectAll}
                />
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.deviceName')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.startDate')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.endDate')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.energyBegin')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.energyEnd')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.energy')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.amount')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.amountVAT')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.invoices.amountWithVAT')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dataTable.map((item, index) => {
              const isSelected = selectedIds && selectedIds[item.id];
              return (
                <Table.Row
                  key={item.id}
                  id={`row${item.id}`}
                  className={classNames(styles.contentHoverable, isSelected && styles.selected)}
                >
                  <Table.Cell collapsing>
                    <span className={isSelected ? styles.numberHideTarget : styles.numberTarget}>
                      {index + 1}
                    </span>
                    <Checkbox
                      fitted
                      checked={isSelected}
                      onChange={handleOnRowSelect}
                      id={item.id}
                      className={classNames(!isSelected && styles.checkboxTarget)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.itemName}>{item.deviceName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_update}>
                      {t('format:date', {
                        postProcess: 'formatDate',
                        value: new Date(item.startDate),
                      })}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_update}>
                      {t('format:date', {
                        postProcess: 'formatDate',
                        value: new Date(item.endDate),
                      })}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_energy}>
                      {item.energyBegin !== null
                        ? (item.energyBegin * 1).toLocaleString('en-GB', {
                            minimumFractionDigits: item.item?.decimalPoint || 0,
                            maximumFractionDigits: item.item?.decimalPoint || 2,
                          })
                        : '-'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_energy}>
                      {item.energyEnd !== null
                        ? (item.energyEnd * 1).toLocaleString('en-GB', {
                            minimumFractionDigits: item.item?.decimalPoint || 0,
                            maximumFractionDigits: item.item?.decimalPoint || 2,
                          })
                        : '-'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_energy}>
                      {(item.energy * 1).toLocaleString('en-GB', {
                        minimumFractionDigits: item.item?.decimalPoint || 0,
                        maximumFractionDigits: item.item?.decimalPoint || 2,
                      })}{' '}
                      {item.unitName}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_amount}>
                      {Math.round(item.sum * 1).toLocaleString('en-GB')} {item.currency}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_amount}>
                      {Math.round(item.sum * 0.1).toLocaleString('en-GB')} {item.currency}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.item_amount}>
                      {Math.round(item.sum * 1.1).toLocaleString('en-GB')} {item.currency}
                    </span>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <div className={`text-right ${styles.pagination_wrapper}`}>
        <Pagination
          activePage={currentPage}
          boundaryRange={0}
          onPageChange={onPageInvoiceChange}
          size="mini"
          siblingRange={1}
          prevItem={{ content: <Icon name="angle left" />, icon: true }}
          nextItem={{ content: <Icon name="angle right" />, icon: true }}
          firstItem={{ content: <Icon name="angle double left" />, icon: true }}
          lastItem={{ content: <Icon name="angle double right" />, icon: true }}
          totalPages={totalPages}
        />
      </div>
    </Tab.Pane>
  );
});

InvoicePane.propTypes = {
  defaultData: PropTypes.arrayOf(PropTypes.any).isRequired,
  name: PropTypes.string.isRequired,
  isEditable: PropTypes.bool.isRequired,
};

export default InvoicePane;
